import { BsThreeDots } from 'react-icons/bs';

export default function Background() {
    return (
        <div>
            <div
                className="section-padding flex flex-col lg:flex-row bg-cream font-body justify-between gap-8 sm:gap-10 lg:gap-12"
            >
                <div className="w-full lg:w-auto flex justify-between lg:flex-col lg:gap-8 lg:justify-normal">
                    <img className="rounded-2xl w-[45%] md:w-1/3 h-auto lg:w-auto" src="simone_selfie.jpg"/>
                </div>
                <div className="flex flex-col gap-4 sm:gap-5 text-slate-800">
                    <h1 className="text-3xl lg:text-4xl font-header font-bold mt-2">
                        Simone Homeniuk
                    </h1>
                    <p className="text-lg lg:text-xl mt-1">
                        <b>Thought Leader and Wisdom Coach</b>
                    </p>
                    <p className="lg:text-lg">
                        I am a <b>HERSTORIAN</b>—a historian by training and a student of humanity by vocation.
                        I study patterns in human behaviour across time, culture, and systems.
                        <b><i> HERSTORY + HISTORY = HUMANITY</i></b>, and understanding these patterns is essential if
                        we are to interrupt cycles of harm and consciously choose a different future.
                    </p>
                    <p className="lg:text-lg">
                        I am a German woman raised in the long shadow of a devastating historical legacy.
                        The values that shaped 20th-century totalitarianism—obedience without inquiry, performance over
                        integrity, competitiveness over compassion, surface order masking internal decay—did not
                        disappear with the end of a regime.
                        History shows us that when curiosity is suppressed and humanity is reduced to productivity,
                        societies fracture from the inside out.
                    </p>
                    <p className="lg:text-lg">
                        Humanity is struggling.
                        We are living through a reckoning—one that calls for courage, reflection, and wisdom rather than
                        fear or force.
                        As the lyric from <i>Coldplay’s “Clocks”</i> asks: <i>“Are we part of the cure or part of the
                        disease?”</i>
                        That question remains urgent.
                    </p>
                    <p className="lg:text-lg">
                        I am a <b>Wisdom Coach, trauma-informed specialist, and educator with 30 years of
                        experience</b> across three Canadian provinces.
                        I have worked with thousands of students and witnessed a consistent and troubling pattern: very
                        few adolescents possess an unshakeable sense of self-worth.
                        Too many young people feel disconnected from their value, their voice, and their belonging.
                        This journey is also personal—it took me many years to fully claim my own self-worth, guided by
                        courageous people who embodied it before I could.
                    </p>
                    <p className="lg:text-lg">
                        I am also a <b>Jay Shetty Certified Life Coach</b>, having completed the Jay Shetty
                        Certification School—a global learning experience rooted in a simple and radical premise: <i>make
                        wisdom go viral</i>.
                        Wisdom, when lived relationally and shared collectively, has the capacity to heal not just
                        individuals, but systems.
                    </p>
                    <p className="lg:text-lg">
                        <b>I do this work as an <u>Act of Truth and ReconciliACTION</u></b>—<b>truth</b>-telling about
                        the systems and patterns that have harmed <b>HUMANITY</b>,
                        and <b><u>reconciliACTION</u></b> through daily, relational practices that restore dignity,
                        curiosity, and connection.
                        This work is grounded in deep respect for <b><u>Indigenous ways of knowing</u></b>, particularly
                        the role of oral storytelling, community wisdom, and collective responsibility—traditions that
                        have sustained humanity for generations.
                    </p>
                    <p className="lg:text-lg">
                        My life’s work sits at the intersection of <b>history, trauma-informed education, wisdom
                        traditions, and human connection</b>.
                        I am committed to interrupting intergenerational trauma and to co-creating new legacies rooted
                        in community, curiosity, care, and belonging—for our children, and for the generations yet to
                        come.
                    </p>
                </div>
            </div>
            <div
                className="section-padding flex flex-col-reverse lg:flex-row bg-cream font-body justify-between gap-8 sm:gap-10 lg:gap-12"
            >
                <div className="flex flex-col gap-4 sm:gap-5 text-slate-800">
                    <h1 className="text-3xl lg:text-4xl font-header font-bold mt-2">
                        Jessie Chen
                    </h1>
                    <p className="text-lg lg:text-xl mt-1">
                        <b>Empowerment Coach, Educator & Systems Thinker</b>
                    </p>
                    <p className="lg:text-lg">
                        I am an educator and systems thinker who studies the patterns of institutions—schools,
                        organizations, families—and why so many of them struggle under structures never designed for
                        human flourishing. I came to this work because I kept seeing the same heartbreaking
                        pattern: brilliant, caring people trapped in broken systems. Teachers forced to police
                        instead of nurture. Leaders constrained by bureaucracy instead of empowered to listen.
                        Young people met with compliance instead of connection.
                    </p>
                    <p className="lg:text-lg">
                        My understanding of why systems fail—and what it takes to heal them—was transformed when
                        I began studying the brain. The brain is a connection machine. It grows through safety,
                        attunement, and repetition. But when a person or system operates from threat, the
                        brain's wiring shifts. Neural pathways for creativity and self-worth get pruned;
                        pathways for fear and defensiveness grow stronger. We were asking people to learn, to
                        grow, to heal—while their nervous systems were signaling danger.
                    </p>
                    <p className="lg:text-lg">
                        <b>Neurogenesis</b>—the brain's ability to grow new connections throughout life—became both
                        a scientific foundation and a mission. If the brain can rewire, then so can
                        institutions. If neural pathways can be reshaped through safe relationship and
                        repeated practice, then so can the patterns of a school, a team, a family. Healing is
                        not about erasing the past. It is about building new connections.
                    </p>
                    <p className="lg:text-lg">
                        Over time, I came to see that the fractures I was witnessing ran deeper than policy.
                        Trauma does what no system should: it severs connections. It disconnects us from our
                        bodies, our emotions, other people, meaning, and ultimately—for many—from the sacred.
                        As an <b>Empowerment Coach</b>, I believe healing is <b>reconnection</b>. And I have found deep
                        resonance in ancient wisdom—including the Bible's vision of <i>shalom</i>: everything in its
                        right place, every relationship restored, every fracture mended.
                    </p>
                </div>
                <div className="w-full lg:w-auto flex justify-between lg:flex-col lg:gap-8 lg:justify-normal">
                    <img className="rounded-2xl w-[45%] md:w-1/3 h-auto lg:w-auto" src="jessie_selfie.JPG"/>
                </div>
            </div>
            <div
                className="section-padding flex flex-col-reverse lg:flex-row bg-cream font-body justify-between gap-8 sm:gap-10 lg:gap-12"
            >
                <div className="flex flex-col gap-4 sm:gap-5 text-slate-800 text-center">
                    <h1 className="text-3xl lg:text-4xl font-header font-bold mt-2 underline">
                        Our Shared Vision:
                    </h1>
                    <p className="lg:text-lg">
                        When Simone and I came together, something powerful happened. She brings the deep
                        wisdom of history, intergenerational patterns, and trauma-informed care. I bring the
                        practical lens of how to redesign institutions so they actually work for the humans
                        inside them. Together, we ask a radical question: <i>What if we built systems that
                        assumed every person has wisdom, not deficits? What if our schools, workplaces, and
                        communities were designed for connection instead of compliance?</i>
                    </p>
                    <p className="lg:text-lg">
                        Arise & Shine is my answer to the question that keeps me awake at night:
                        <i> How do I use everything I know—about the brain, about trauma, about systems, about the
                            healing power of connection—to help the people who are struggling the most, as quickly
                            as possible?</i>
                    </p>
                    <p className="lg:text-lg">
                        We are not here to add another program. We are here to shift the operating system—to
                        help people and institutions move from burnout to belonging, from reactivity to
                        relationship, from performance to purpose. This is my story, written alongside Simone,
                        and alongside everyone who dares to believe that another way is possible.
                    </p>
                </div>
            </div>
        </div>
    );
}